import { Box, Divider, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { InputLeftElement } from '@chakra-ui/react';
import { ReactComponent as WorldIcon } from 'assets/icons/mapIcons/world.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { UPGRADE_REDIRECT_LINK } from 'constants/links';
import { MAP_CREATED, MAP_IMPORT, UPGRADE } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getUserPlanProperty } from 'libs/third-party/Mixpanel/mixpanel.helper';
import { getInitialWidgetPosition } from 'modules/Editor/Editor.helpers';
import { SIDE_MENU_WIDGET_TYPES } from 'modules/Editor/components/SideMenuSettings/SideMenuSettings.config';
import { addNewWidget, useAppDispatch } from 'modules/Editor/store';
import { setWidgetSettingsView } from 'modules/Editor/store/editorSettingsSlice';
import { PermissionBadge, hasResourcePermission, useUser } from 'modules/User';
import {
  AutoCompleteInput,
  AutoCompleteInputItem,
  AutoCompleteInputList,
} from 'modules/common/components/AutoCompleteInput';
import { AutoCompleteClearButton } from 'modules/common/components/AutoCompleteInput/AutoCompleteClearButton';
import { AutoCompleteInputGroup } from 'modules/common/components/AutoCompleteInput/AutoCompleteInput';
import { useToast } from 'modules/common/components/Toast';
import { ReactElement, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isDevelopment } from 'utils/environment';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { generateDefaultData as generateMapData } from 'widgets/MapWidget/MapWidget.helpers';

import {
  ALL_MAP_CONFIGS,
  ALL_MAP_TITLES,
  DEFAULT_MAP_CONFIGS,
  DEFAULT_MAP_WIDGET_MENU_BUTTON_PERMISSION,
  MAP_IMPORT_ERROR_TOAST,
} from './MapWidgetMenu.config';
import { getButtonConfigs, getFilteredRegionSuggestions } from './MapWidgetMenu.helpers';
import {
  ImportedMap,
  MapButtonProps,
  MapCreationType,
  MapMenuButtonConfig,
  MapWidgetSearchInputProps,
  PopularMapsLabelProps,
  SelectionGridProps,
} from './MapWidgetMenu.types';

// import { PremiumMapUpgradeModal } from './PremiumMapUpgradeModal';

// Todo: adding back the premium map upgrade modal when other charts have it as well.
export const MapWidgetMenu = (): ReactElement => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.mapsMenu' });
  const { userType } = useUser();
  const toast = useToast();

  const dispatch = useAppDispatch();

  // The alphabets being typed into the search input
  const [searchKeyword, setSearchKeyword] = useState('');

  // The chosen term that comes from clicking on the search suggestions, e.g. Arkansas
  const [chosenTerm, setChosenTerm] = useState('');

  const fetchMap = useCallback(
    async (config: MapMenuButtonConfig) => {
      try {
        // Todo: remove highcharts/map-collection library import
        const importedMapResponse = await fetch('https://code.highcharts.com/mapdata/' + config.importMapUrl);

        if (importedMapResponse.ok) {
          const importedMap: ImportedMap = await importedMapResponse.json();
          return importedMap;
        }
      } catch (error) {
        if (isDevelopment) console.error('Error fetching map data:', error);
        Mixpanel.track(MAP_IMPORT.eventName, { map_import_status: MAP_IMPORT.map_import_status.fail });
        toast(MAP_IMPORT_ERROR_TOAST);
      }
    },
    [toast],
  );

  const handleAddWidget = useCallback(
    async (config: MapMenuButtonConfig, buttonType: MapCreationType) => {
      const { top, left } = getInitialWidgetPosition();

      // Here we fetch the map json file by its name when the button is clicked on.
      const widgetData = generateMapData(
        await fetchMap(config),
        {
          top,
          left,
        },
        config.label,
      );

      if (widgetData) {
        dispatch(addNewWidget(widgetData));

        Mixpanel.track(MAP_CREATED.eventName, {
          from: buttonType,
          region: config.label,
        });

        if (SIDE_MENU_WIDGET_TYPES.includes(widgetData.widgetType)) {
          dispatch(setWidgetSettingsView(true));
        }
      }
    },
    [dispatch, fetchMap],
  );

  const onGated = useCallback(() => {
    Mixpanel.track(UPGRADE.eventName, {
      Feature: UPGRADE.feature.PremiumChart,
      'User Plan': getUserPlanProperty(userType),
    });

    setAnalyticsUpgradeFeatureProp(UPGRADE.feature.PremiumMap, 'map widget');
    window.open(`${UPGRADE_REDIRECT_LINK}/${DEFAULT_MAP_WIDGET_MENU_BUTTON_PERMISSION}`, '_self');
  }, [userType]);

  const onClickUpgrade = useCallback(onGated, [onGated]);

  return (
    <Flex padding={4} direction={'column'} gap={4} data-testid={'widget-menu-map-panel'} h={'full'} overflow={'auto'}>
      <MapWidgetSearchInput
        searchKeyword={searchKeyword}
        chosenTerm={chosenTerm}
        setSearchKeyword={setSearchKeyword}
        setChosenTerm={setChosenTerm}
      />
      {chosenTerm.length > 0 && (
        <SelectionGrid
          onClick={handleAddWidget}
          onClickUpgrade={onClickUpgrade}
          chosenTerm={chosenTerm}
          mapCreationType={MapCreationType.Search}
        />
      )}
      <PopularMapsLabel label={t('popularMapsLabel')} />
      <SelectionGrid
        onClick={handleAddWidget}
        onClickUpgrade={onClickUpgrade}
        mapCreationType={MapCreationType.Popular}
      />
    </Flex>
  );
};

const MapWidgetSearchInput = ({
  searchKeyword,
  chosenTerm,
  setSearchKeyword,
  setChosenTerm,
}: MapWidgetSearchInputProps): ReactElement => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.mapsMenu' });
  const filteredRegionSuggestions = useMemo(
    () => getFilteredRegionSuggestions(ALL_MAP_TITLES, searchKeyword),
    [searchKeyword],
  );

  if (!searchKeyword.trim() && chosenTerm.length) {
    setChosenTerm('');
  }

  const onClearSearch = useCallback(() => {
    setSearchKeyword('');
    setChosenTerm('');
  }, [setChosenTerm, setSearchKeyword]);

  const handleInputChange = useCallback(
    (inputs: string[]) => {
      if (inputs.length === 0) return;
      setChosenTerm(inputs[0]);
    },
    [setChosenTerm],
  );

  const onIndividualInputChange = (text: string) => setSearchKeyword(text);

  return (
    <Flex flexDirection={'column'} gap={filteredRegionSuggestions.length === 0 ? 0 : 4}>
      <AutoCompleteInput
        placeholder={t('searchBarLabel')}
        onChange={handleInputChange}
        onIndividualInputChange={onIndividualInputChange}
        openListOnFocus
        submitValueOnEnter
        defaultValue={searchKeyword}
      >
        <AutoCompleteInputGroup>
          <InputLeftElement>
            <SearchIcon stroke='var(--vg-colors-upgrade-blue-500' />
          </InputLeftElement>
          <AutoCompleteClearButton onClearInputEnd={onClearSearch} />
        </AutoCompleteInputGroup>

        <AutoCompleteInputList showNoResultsFound useRelativePosition>
          {filteredRegionSuggestions.map((region, index) => (
            <AutoCompleteInputItem key={region} value={region} name={region}>
              <Box
                _hover={{
                  background: 'black-alpha.200',
                }}
                _focus={{
                  background: 'black-alpha.200',
                }}
                key={index}
                paddingY={1}
                paddingX={2}
                borderRadius={4}
                width={'100%'}
                height={'100%'}
                textAlign={'left'}
                fontSize={12}
                lineHeight={'16px'}
                textOverflow={'ellipsis'}
                overflow={'hidden'}
              >
                {region}
              </Box>
            </AutoCompleteInputItem>
          ))}
        </AutoCompleteInputList>
      </AutoCompleteInput>
    </Flex>
  );
};

const PopularMapsLabel = ({ label }: PopularMapsLabelProps): ReactElement => {
  return (
    <Text fontSize={'sm'} lineHeight={5} fontWeight={'semibold'} _hover={{ cursor: 'default' }}>
      {label}
    </Text>
  );
};

const MapButton = memo(
  ({ menuButtonConfig, onClick, permission, isAccessAllowed, buttonType }: MapButtonProps): ReactElement => {
    const onMapButtonClick = () => onClick(menuButtonConfig, buttonType);
    return (
      <Flex position={'relative'}>
        {!isAccessAllowed && <PermissionBadge permission={permission} position={'absolute'} top={-2} right={2} />}
        <Box
          as='button'
          width={'100%'}
          minHeight={28}
          borderRadius='base'
          borderColor='gray.100'
          padding={3}
          transitionDuration='100ms'
          outline='1px solid'
          outlineColor='gray.100'
          _hover={{ bg: 'hover.gray' }}
          _focus={{
            backgroundColor: 'gray.100',
            outline: '2px solid',
            outlineColor: 'upgrade.500',
          }}
          onClick={onMapButtonClick}
        >
          <Flex direction={'column'} alignItems={'center'} height={'fit-content'} gap={2}>
            <Box height={'64px'}>{menuButtonConfig.icon ? menuButtonConfig.icon : <WorldIcon />}</Box>
            <Text fontWeight={'normal'} fontSize={'xs'} lineHeight={4}>
              {menuButtonConfig.label}
            </Text>
          </Flex>
        </Box>
      </Flex>
    );
  },
);
MapButton.displayName = 'MapButton';

const SelectionGrid = memo(({ onClick, onClickUpgrade, chosenTerm, mapCreationType }: SelectionGridProps) => {
  const { userType } = useUser();

  const buttonConfig = useMemo(() => getButtonConfigs(chosenTerm, DEFAULT_MAP_CONFIGS, ALL_MAP_CONFIGS), [chosenTerm]);

  return (
    <>
      <SimpleGrid columns={2} spacing={4}>
        {buttonConfig.map((config, i) => {
          const isAccessAllowed = hasResourcePermission(
            userType,
            config.permission ? config.permission : DEFAULT_MAP_WIDGET_MENU_BUTTON_PERMISSION,
          );
          return (
            <MapButton
              key={i}
              onClick={isAccessAllowed ? onClick : onClickUpgrade}
              menuButtonConfig={config}
              permission={config.permission ? config.permission : DEFAULT_MAP_WIDGET_MENU_BUTTON_PERMISSION}
              isAccessAllowed={isAccessAllowed}
              buttonType={mapCreationType}
            />
          );
        })}
      </SimpleGrid>
      {chosenTerm && <Divider />}
    </>
  );
});
SelectionGrid.displayName = 'SelectionGrid';
